// Pure derivations for the entity-resolution review surface. The per-member
// similarity score is computed and persisted in the backend at grouping time
// (SP-362) — the panel renders that stored value verbatim and never recomputes
// it. Everything else here (score bands, shared n-grams, corroborating fields)
// is presentational, derived from the members' names + properties.

import type { GroupMember, ResolutionGroup } from '@/services/api/entity-resolution-groups'
import { getEntityDisplayLabel } from '@/utils/entity-display'
import { isHiddenPropertyKey } from '@/utils/system-properties'

// Identity keys rendered elsewhere. System stamps and derived keys such as
// `name_variants` (ADR-0051) go through `isHiddenPropertyKey`.
const INTERNAL_KEYS = new Set(['name', 'id', 'case_id', 'original_name'])

const isInternalKey = (key: string): boolean => isHiddenPropertyKey(key) || INTERNAL_KEYS.has(key)

/**
 * Human-friendly label for a group member. Delegates to the shared
 * `getEntityDisplayLabel`, which honours the `is_display_name` schema marker
 * (via the `_display_name` write-time stamp) and per-type fallbacks
 * (e.g. BankAccount → `account_number`/`iban`). The backend coalesces a member
 * with no `name` property to the literal 'Unknown', so rendering `member.name`
 * directly is wrong for types like BankAccount — always go through this.
 */
export function memberDisplayName(member: GroupMember): string {
  return getEntityDisplayLabel({
    id: member.id,
    name: member.name,
    labels: member.labels,
    properties: member.properties,
  })
}

/** Real, non-internal (key, value) pairs of an entity, declaration order. */
function realProps(props: Record<string, unknown>): [string, string][] {
  return Object.entries(props)
    .filter(([k, v]) => !isInternalKey(k) && v != null && String(v).trim())
    .map(([k, v]) => [k, String(v)] as [string, string])
}

export type ScoreBand = 'exact' | 'high' | 'mid'
export type CompareStatus = 'approx' | 'equal' | 'different' | 'missing'

export interface CompareRow {
  key: string
  label: string
  left: string | null
  right: string | null
  status: CompareStatus
}

export interface Corroboration {
  label: string
  ok: boolean
}

// ── Scores & bands ───────────────────────────────────────────────────────────

/** Format a 0..1 score as a whole-percentage string; `null` (unscored) → "—". */
export function percent(score: number | null | undefined): string {
  if (score == null || !Number.isFinite(score) || score < 0 || score > 1) return '—'
  const rounded = Math.round(score * 100)
  return score < 1 && rounded === 100 ? '<100%' : `${rounded}%`
}

/** Stored scores describe the original representative, regardless of the chosen survivor. */
export function minimumGroupSimilarity(group: ResolutionGroup): number | null {
  if (!group.members.some(member => member.id === group.representativeId)) return null
  const scores = group.members
    .filter(member => member.id !== group.representativeId)
    .map(member => member.similarityScore)
  if (
    !scores.length ||
    scores.some(score => score == null || !Number.isFinite(score) || score < 0 || score > 1)
  )
    return null
  return Math.min(...scores.filter((score): score is number => score != null))
}

/** Bucket a 0..1 similarity into a colour band; `null` (unscored) → "mid". */
export function scoreBand(sim: number | null | undefined): ScoreBand {
  if (sim == null) return 'mid'
  if (sim === 1) return 'exact'
  if (sim >= 0.8) return 'high'
  return 'mid'
}

export const BAND_AVATAR: Record<ScoreBand, string> = {
  exact: 'bg-success-surface text-success-foreground',
  high: 'bg-info-surface text-info-foreground',
  mid: 'bg-warning-surface text-warning-foreground',
}

export const BAND_BAR: Record<ScoreBand, string> = {
  exact: 'bg-success',
  high: 'bg-info',
  mid: 'bg-warning',
}

export const BAND_TEXT: Record<ScoreBand, string> = {
  exact: 'text-success',
  high: 'text-info',
  mid: 'text-warning',
}

/** Up to `max` character trigrams the two names share (the blocking signal). */
export function sharedNgrams(a: string, b: string, max = 8): string[] {
  const grams = (s: string) => {
    const norm = s.toLowerCase().replace(/[^a-z0-9]/g, '')
    const out = new Set<string>()
    for (let i = 0; i + 3 <= norm.length; i++) out.add(norm.slice(i, i + 3))
    return out
  }
  const ga = grams(a)
  const gb = grams(b)
  return [...ga].filter(g => gb.has(g)).slice(0, max)
}

// ── Avatars & labels ─────────────────────────────────────────────────────────

/** One- or two-letter initials for an avatar. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** A one-line summary of the candidate's first real properties, or — when it has
 *  none — its connection count. Fully data-driven: no assumed property names. */
export function candidateSubtitle(member: GroupMember): string {
  const props = realProps(member.properties)
  if (props.length === 0) return `${member.degree} connection${member.degree === 1 ? '' : 's'}`
  return props
    .slice(0, 2)
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
    .join(' · ')
}

// ── Head-to-head comparison ──────────────────────────────────────────────────

function statusOf(left: string | null, right: string | null): CompareStatus {
  if (left === null || right === null) return 'missing'
  return left === right ? 'equal' : 'different'
}

/** Property-by-property comparison rows: NAME + TYPE first, then every other
 *  non-internal property, differences before matches. */
export function compareRows(survivor: GroupMember, member: GroupMember): CompareRow[] {
  const rows: CompareRow[] = [
    {
      key: 'name',
      label: 'Name',
      left: memberDisplayName(survivor),
      right: memberDisplayName(member),
      status: 'approx',
    },
    {
      key: 'type',
      label: 'Type',
      left: survivor.labels[0] ?? null,
      right: member.labels[0] ?? null,
      status: statusOf(survivor.labels[0] ?? null, member.labels[0] ?? null),
    },
  ]

  const keys = new Set([...Object.keys(survivor.properties), ...Object.keys(member.properties)])
  const propRows: CompareRow[] = []
  keys.forEach(key => {
    if (isInternalKey(key)) return
    const left = survivor.properties[key] != null ? String(survivor.properties[key]) : null
    const right = member.properties[key] != null ? String(member.properties[key]) : null
    if (left === null && right === null) return
    propRows.push({
      key,
      label: key.replace(/_/g, ' '),
      left,
      right,
      status: statusOf(left, right),
    })
  })
  propRows.sort((a, b) => {
    const rank = (s: CompareStatus) => (s === 'different' ? 0 : s === 'missing' ? 1 : 2)
    return rank(a.status) - rank(b.status) || a.key.localeCompare(b.key)
  })
  return [...rows, ...propRows]
}

/** Fields the two entities both carry, marked ✓ when their values agree. Fully
 *  data-driven from the real properties — no assumed property names. */
export function corroborations(survivor: GroupMember, member: GroupMember): Corroboration[] {
  const keys = new Set([...Object.keys(survivor.properties), ...Object.keys(member.properties)])
  const out: Corroboration[] = []
  keys.forEach(key => {
    if (isInternalKey(key)) return
    const left = survivor.properties[key]
    const right = member.properties[key]
    if (left == null || right == null || !String(left).trim() || !String(right).trim()) return
    out.push({ label: `Same ${key.replace(/_/g, ' ')}`, ok: String(left) === String(right) })
  })
  return out
}
